/** Close lead custom field IDs for the live skip sidecar (Sovereign Overflow). */
import { normalizeNcForeclosureCaseNumber } from "./nc-court-case.js";

export const CLOSE_SKIP_ORG_NAME = "Sovereign Overflow";
export const CLOSE_LEAD_BRIEF_FIELD_NAME = "Lead brief";
export const CLOSE_AUCTION_EQUITY_FIELD_NAME = "Auction equity";
/** Live Close field description. Replaces the SO-5 EPLV × 115% copy. */
export const CLOSE_AUCTION_EQUITY_FIELD_DESCRIPTION =
  "Current auction amount minus Affidavit of Default principal.";
export const CLOSE_AUCTION_EQUITY_FIELD_DESCRIPTION_LEGACY =
  "Auction price minus (EPLV × 115%).";
export const CLOSE_LEAD_BRIEF_ORIGIN = "https://county-preforeclosure-control-plane.santateresa.workers.dev";

export function closeLeadBriefUrl(sp: string | null | undefined): string | null {
  const normalized = normalizeNcForeclosureCaseNumber(String(sp ?? ""));
  if (!normalized) return null;
  return `${CLOSE_LEAD_BRIEF_ORIGIN}/brief/${encodeURIComponent(normalized)}`;
}

export const CLOSE_SKIP_LEAD_STATUS_POTENTIAL =
  "stat_AELvpETSvZWGrouaFmVVOwQ3ISjIOSott8ABB8QB81a";

/** Live GET /status/lead/ on the closeio2 key, 2026-08-25. */
export const CLOSE_SKIP_LEAD_STATUS_READY_TO_CALL =
  "stat_fTvoc1RAcDzrgqbY70LmO1y2kOkZkSVrP40uXOq1tgm";

export const CLOSE_SKIP_LEAD_STATUS_ON_HOLD =
  "stat_6z1hcLyqJKxMq2Vk8zqgPjT6uc6CE3fn3j00pUrPVQ6";

/** Labels in the live Close org. Status IDs are resolved at write time; frozen IDs above are fallbacks only if they still exist. */
export const CLOSE_SKIP_PIPELINE_STATUS_LABELS = {
  potential: "Potential",
  ready_to_call: "Ready to Call",
  on_hold: "On Hold"
} as const;

export type CloseSkipPipelineStatus = keyof typeof CLOSE_SKIP_PIPELINE_STATUS_LABELS;

export const CLOSE_SKIP_PIPELINE_STATUS_ALIASES: Record<CloseSkipPipelineStatus, readonly string[]> = {
  potential: ["Potential"],
  ready_to_call: ["Ready to Call", "Ready to call"],
  on_hold: ["On Hold", "On hold"]
};

export const CLOSE_SKIP_PIPELINE_STATUS_FALLBACK_IDS: Record<CloseSkipPipelineStatus, string> = {
  potential: CLOSE_SKIP_LEAD_STATUS_POTENTIAL,
  ready_to_call: CLOSE_SKIP_LEAD_STATUS_READY_TO_CALL,
  on_hold: CLOSE_SKIP_LEAD_STATUS_ON_HOLD
};

/** Caller-set Close statuses. IDs are resolved/created at runtime by label.
 * PI and Genealogist are open pipeline statuses — not won/lost — so the lead comes back. */
export const CLOSE_SKIP_CALLER_STATUS_LABELS = {
  given_to_pi: "PI",
  given_to_genealogist: "Genealogist",
  follow_up: "Follow up",
  passed: "Passed"
} as const;

export type CloseSkipCallerDisposition = keyof typeof CLOSE_SKIP_CALLER_STATUS_LABELS;

export const CLOSE_SKIP_CALLER_STATUS_ALIASES: Record<CloseSkipCallerDisposition, readonly string[]> = {
  given_to_pi: ["PI", "Given to PI", "PI Status"],
  given_to_genealogist: ["Genealogist", "Given to Genealogist"],
  follow_up: ["Follow up", "Follow-up"],
  passed: ["Passed"]
};

export const CLOSE_SKIP_CALLER_DISPOSITIONS = Object.keys(CLOSE_SKIP_CALLER_STATUS_LABELS) as CloseSkipCallerDisposition[];

export function isCloseSkipCallerDisposition(value: unknown): value is CloseSkipCallerDisposition {
  return typeof value === "string" && value in CLOSE_SKIP_CALLER_STATUS_LABELS;
}

export const CLOSE_SKIP_FIELD_IDS = {
  // Visible to callers. Live GET /custom_field/lead/ on Sovereign Overflow, 2026-08-28.
  leadBrief: "cf_TgPfZaIymUjx1yx1dNQz71rO7TjMbAQ5E3oEDsPFSzu",
  caseSpNumber: "cf_p4KikhYkpvXkCpRvBV5ae1QhXhsaMdjw5H2ds7xwuft",
  owner: "cf_RDjZxNUW0TaAGUoaXcrDCU46MG16NrEkEEzEh6mEMOv",
  estimatedEquity: "cf_FibFpzgpVP0Ax0oghT0dW1ZsStqNimzQUolW2pl1CC2",
  county: "cf_5wfY6bCP3LRFlZMKVyZcDWjPu0WXc433WAOBKioP5XA",
  auctionDate: "cf_JRNJqjS9CrskqJWI3a1gA1GHI0UWISeVTGURdKdzw6i",
  obituaryLink: "cf_pzNcFiz7RNEfkbgUu9YeKhGQdRa8uKyMhjlGyf4lIFN",
  // Hidden integration fields.
  parcel: "cf_2U0lBzrMOzqhRFPJd1ZuDUwXo1Gpz3EnKGaU4SN5Woi",
  mailing: "cf_ffrX6yrazAa5A7lIfLQy1Cx2DprMLE4pWmUVAK1pOLg",
  bid: "cf_mXMjJTnPcrhUWB3WmRbW7yRaphtHFgyHyQq2vI7YRQg",
  deceased: "cf_0eEgADuB30DOYCtOvPVCSK19rS6iXLtoXuoZSSjyMHo",
  plv: "cf_7NCL3bO2i5Xq7MSN3L9qJKybjJNFDWCTZUYjzQbjeQm",
  avm: "cf_D85RZdYMfBZhUJ6VpVUpVLZDELrltovgumMev4Gs7l5",
  zestimate: "cf_GG3TLlYokfiVoVM4pwUP2htyOuEAvW9t2ZY5oto4My5",
  sourceTree: "cf_0Aao73lllzPyzgFXgRyQlHm6LrbjkCVFaqAqM3JUYH8",
  sourceReference: "cf_Chpk9Bu0A5WC78nzXOyPkKzqYiOwXG6KgpDaq6ArGta",
  sourcePayload: "cf_rNPLKPCH8gTHmHzkSrWoI7fNnknr6zdKon8ezFVk1K5",
  smartskipPayload: "cf_n8iexVopddebS05FwGQCCZF5FLbGpsMBH4dEvOE2ryI",
  skipTraceDate: "cf_Ky6XSoeA4qUkcaxBHNuQTR7WBA3kYHUlymwKCOvAd8l",
  skipTraceStatus: "cf_cLcZno9WmD3aIPnKnkeZuECnphTymcfLgwHX25185x2",
  moreOwners: "cf_PDrPIMC7YJG7ULCTsJwywgkTdBfhOBW87ITFiXbEnod",
  active: "cf_SIGK9nNDsFNmE37h0qq8I4VGNeMBhE4JpAJWSYt45yk",
  // Legacy aliases kept so existing callers/tests still resolve IDs.
  parcelId: "cf_2U0lBzrMOzqhRFPJd1ZuDUwXo1Gpz3EnKGaU4SN5Woi",
  attomMailingAddress: "cf_ffrX6yrazAa5A7lIfLQy1Cx2DprMLE4pWmUVAK1pOLg",
  deceasedStatusSkip: "cf_0eEgADuB30DOYCtOvPVCSK19rS6iXLtoXuoZSSjyMHo",
  attomOwner: "cf_GXo53AVNBlInMZoHeiAh91RZipjip0n3x7YdhM92yb2",
  importNotes: "cf_fYbXti3bZnlUTC4TQN4Jy2edJo60c3dBQNWE7Izzwil",
  daysToAuction: "cf_sVUAGaTT6ziAq96yR4LnsynU7OW8Gjy3yc3iMoutvTD"
} as const;

export const CLOSE_SKIP_VISIBLE_FIELD_IDS = [
  CLOSE_SKIP_FIELD_IDS.leadBrief,
  CLOSE_SKIP_FIELD_IDS.caseSpNumber,
  CLOSE_SKIP_FIELD_IDS.owner,
  CLOSE_SKIP_FIELD_IDS.estimatedEquity,
  CLOSE_SKIP_FIELD_IDS.county,
  CLOSE_SKIP_FIELD_IDS.auctionDate,
  CLOSE_SKIP_FIELD_IDS.obituaryLink
] as const;

const CLOSE_SKIP_KNOWN_FIELD_IDS = new Set<string>(Object.values(CLOSE_SKIP_FIELD_IDS));
const CLOSE_SKIP_LEAD_ASSIGNABLE_FIELD_IDS = new Set<string>(CLOSE_SKIP_VISIBLE_FIELD_IDS);

/** Lead POST/PUT/PATCH may only send Lead-assignable custom field IDs. */
export function isCloseSkipLeadAssignableFieldId(id: string): boolean {
  if (!id) return false;
  if (CLOSE_SKIP_LEAD_ASSIGNABLE_FIELD_IDS.has(id)) return true;
  // Runtime-created Lead fields (Auction equity, live Lead brief) are not in the frozen skip map.
  return !CLOSE_SKIP_KNOWN_FIELD_IDS.has(id);
}

/** Insert a runtime Auction equity field id after Estimated equity. Never invents a Close id. */
export function closeSkipVisibleFieldOrder(auctionEquityFieldId?: string | null): string[] {
  const ids: string[] = [...CLOSE_SKIP_VISIBLE_FIELD_IDS];
  const insert = String(auctionEquityFieldId ?? "").trim();
  if (!insert || ids.includes(insert)) return ids;
  const at = ids.indexOf(CLOSE_SKIP_FIELD_IDS.estimatedEquity);
  ids.splice(at < 0 ? ids.length : at + 1, 0, insert);
  return ids;
}

export const CLOSE_SKIP_CHOICE_VALUES = ["No", "Unknown", "Yes"] as const;
export type CloseSkipChoice = (typeof CLOSE_SKIP_CHOICE_VALUES)[number];
